import React, { useRef, useMemo, useState, useEffect } from 'react';
import CardSlotV2 from '@/pages/intelligence/components/CardSlotV2';
import ChartsIndex from '@/pages/intelligence/components/ChartsComponents/ChartsIndex';
import useActiveUserLineCharts from '@/pages/intelligence/pages/TopCharts/MobileGames/hooks/useActiveUserLineCharts';
import { ChartType } from '@/pages/intelligence/pages/TopCharts/_config/enums';
import { DATA_SOURCE_NAME_MAP } from '@/pages/intelligence/pages/TopCharts/_config/topChartsConst';

import type { IChartsIndexInstance } from '@intelligence/components/ChartsComponents/ChartsIndex';

const getSortedMetrics = (
  metrics?: any[],
  cfg?: { dataSources: string[] },
): any[] => {
  if (!Array.isArray(metrics)) return [];
  const { dataSources } = cfg || {};
  return metrics.sort((a: any, b: any): number => {
    const aIndex = dataSources?.indexOf(a?.source) || -1;
    const bIndex = dataSources?.indexOf(b?.source) || -1;
    return aIndex - bIndex;
  });
};

// 补全缺失的指标对象，目前改方法只补全缺失的dau指标对象
const getFullDauMetrics = (
  metrics?: any[],
  cfg?: { title: string; dataSources: string[] },
): any[] => {
  if (!Array.isArray(metrics)) return [];
  const { title, dataSources } = cfg || {};
  const dauMetricsSources = metrics.map((item) => item?.source);
  const lostDauMetricsSources = dataSources?.filter(
    (item) => !dauMetricsSources.includes(item),
  );
  if (!lostDauMetricsSources?.length) return metrics;
  const fullDauMetrics = [...metrics];

  for (const lostDauMetricSource of lostDauMetricsSources) {
    const lostDauMetric = {
      source: lostDauMetricSource,
      value: '',
      growth_ratio: '',
      b_predict: false,
      metric: 'dau',
      order_source: lostDauMetricSource,
      order_metric: `${lostDauMetricSource}_dau`,
      titleNow: title,
    };
    fullDauMetrics.push(lostDauMetric);
  }

  return fullDauMetrics;
};

const getLineDataOfComponent = (
  games: any[],
  cfg: { title: string; dataSources: string[] },
): any[] => {
  if (!Array.isArray(games)) return [];
  const result: any[] = [];
  for (const game of games) {
    if (!game || !game?.id || !game?.name) continue;
    const timelines = game?.metric_value_list;
    if (!Array.isArray(timelines)) continue;
    for (const timeline of timelines) {
      const time = timeline?.time;
      let metrics = timeline?.metric_value_list;
      if (!time || !Array.isArray(metrics)) continue;
      metrics = getSortedMetrics(getFullDauMetrics(metrics, cfg), cfg);
      const resultMetrics: any = [];
      for (const metric of metrics) {
        if (!metric) continue;
        resultMetrics.push({
          ...metric,
          b_predict: metric?.b_predict,
          metric: metric?.source,
          order_source: metric.source,
          order_metric: `${metric.source}_${metric.metric}`,
          titleNow: cfg?.title,
          growth_ratio: metric?.growth_ratio,
        });
      }
      result.push({
        ...game,
        time,
        rank_name: game.name,
        metric_value_list: resultMetrics,
      });
    }
  }
  return result;
};

// 每个榜单，组装好各类可视化需要的数据，传递给可视化统一组件
const Visualis: React.FC<any> = (props) => {
  const {
    isVisualisLoading,
    isNodata,
    isShowRangeDayMonth,
    chartType = ChartType.Line,
    topChartsType = '',
    imageType,
    dateType,
    titleNow = '',
    dataSources,
    lineData,
  } = props;

  const chartsInstance = useRef<IChartsIndexInstance>(null);

  const [chartsData, setChartsData] = useState<{
    metricChartlist: any[];
    timelineOfMetricChartlist: any[];
  }>({
    metricChartlist: [],
    timelineOfMetricChartlist: [],
  });

  const dataSourceNames = useMemo<string[]>(() => {
    if (!Array.isArray(dataSources)) return [];
    return dataSources.map((item) => DATA_SOURCE_NAME_MAP[item]);
  }, [dataSources]);

  useEffect(() => {
    const lineDataOfComponent = getLineDataOfComponent(lineData, {
      title: titleNow,
      dataSources,
    });
    setChartsData({
      ...chartsData,
      timelineOfMetricChartlist: lineDataOfComponent,
    });
  }, [titleNow, lineData]);

  // 获取折线图hooks
  const { lineOption } = useActiveUserLineCharts({
    loading: isVisualisLoading,
    chartType,
    chartsInstance,
    isShowRangeDayMonth,
    isMobileCompare: true,
    sendEchartsData: chartsData,
    dataSourceEachers: dataSourceNames,
    topChartsType,
    imageType,
    titleNow,
    dateType,
  });

  return (
    <CardSlotV2 IconList={false}>
      <div className="card-line">
        <ChartsIndex
          ref={chartsInstance}
          chartLoading={isVisualisLoading}
          isNodata={isNodata}
          chartType={chartType}
          lineOption={lineOption}
        />
      </div>
    </CardSlotV2>
  );
};

export default Visualis;
